/**
 * Lógica pura de presupuesto por rubro: comparación contra lo facturado y lo pagado (todo en USD), el formulario de
 * los ítems del cómputo (sólo admin; precios en pesos, pasados a USD con el tipo de cambio del presupuesto) y el
 * resumen de la importación de la planilla. El backend arma la comparación
 * entera (el total de cada rubro es la suma de sus ítems): acá sólo se formatea y se arman los payloads.
 */
import type { BadgeTono } from "@/components";
import type { CeldaComparacion, EstadoPresupuesto, ImportacionPresupuesto, ItemComputo, ItemPresupuesto, Presupuesto, RubroPresupuesto } from "./api";
import { formatMoneda, type Moneda } from "./format";
import { aCentavos, deCentavos, montoAInput, parseMonto } from "./gastos";

export const ETIQUETA_ESTADO: Record<EstadoPresupuesto, string> = {
  sin_presupuesto: "Sin presupuesto",
  ok: "En orden",
  alerta: "Cerca del tope",
  excedido: "Excedido",
};

const TONO_ESTADO: Record<EstadoPresupuesto, BadgeTono> = {
  sin_presupuesto: "neutro",
  ok: "exito",
  alerta: "alerta",
  excedido: "peligro",
};

export function tonoEstado(e: EstadoPresupuesto): BadgeTono {
  return TONO_ESTADO[e];
}

// Un decimal fijo (nunca "50 %" para un 50,0): mismo formato que consumido_pct del backend.
const UN_DECIMAL = new Intl.NumberFormat("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const formatoPct = (n: number) => `${UN_DECIMAL.format(n)} %`;

export function porcentaje(i: ItemPresupuesto): number | null {
  return i.consumido_pct === null ? null : Number(i.consumido_pct);
}

export function textoConsumido(i: ItemPresupuesto): string {
  const pct = porcentaje(i);
  return pct === null ? "—" : formatoPct(pct);
}

/** Facturado en rubros que sí tienen presupuesto: `presupuesto − disponible`, igual a `facturado − facturado_sin_presupuesto`. */
function facturadoConPresupuesto(t: Presupuesto["totales"]): number {
  return Number(t.facturado) - Number(t.facturado_sin_presupuesto);
}

/** (facturado − facturado_sin_presupuesto) / presupuesto, en todos los rubros con presupuesto cargado. "—" si el total de presupuesto es 0. */
export function consumidoTotal(p: Presupuesto): string {
  const presupuesto = Number(p.totales.presupuesto);
  if (presupuesto === 0) return "—";
  return formatoPct((facturadoConPresupuesto(p.totales) / presupuesto) * 100);
}

/** Tarjeta de resumen para Inicio (Task 11): el % consumido total, con el detalle de facturado sobre presupuesto. */
/** El Inicio muestra la tarjeta de presupuesto recién con algún rubro presupuestado o algo facturado. */
export function hayPresupuesto(p: Presupuesto): boolean {
  return Number(p.totales.presupuesto) > 0 || Number(p.totales.facturado) > 0;
}

export function tarjetaPresupuesto(p: Presupuesto): { valor: string; detalle: string } {
  return {
    valor: consumidoTotal(p),
    detalle: `${formatMoneda(facturadoConPresupuesto(p.totales), "USD")} de ${formatMoneda(p.totales.presupuesto, "USD")}`,
  };
}

// ------------------------------------------------------------------ ítems del cómputo (sólo admin escribe)

/** Unidades de la planilla de cómputo; el Select deja escribir otra. */
export const UNIDADES = ["gl", "un", "m2", "m3", "ml", "kg", "mes"] as const;

export function opcionesUnidad(actual: string): { valor: string; etiqueta: string }[] {
  const lista: string[] = [...UNIDADES];
  if (actual && !lista.includes(actual)) lista.push(actual);
  return lista.map((u) => ({ valor: u, etiqueta: u }));
}

/** El form base de un renglón con un solo precio: lo usan las líneas de un presupuesto recibido (`lib/cotizaciones.ts`). */
export interface FormItem {
  descripcion: string;
  unidad: string;
  cantidad: string;
  precio: string;
}

/** El form de un ítem del presupuesto de obra: precio unitario de materiales y de mano de obra, en pesos. */
export interface FormItemObra {
  descripcion: string;
  unidad: string;
  cantidad: string;
  materiales: string;
  mano_obra: string;
}

/** "120.500" → "120,5"; "10.000" → "10" (para editar). */
export function cantidadAInput(cantidad: string): string {
  const [ent = "", dec = ""] = cantidad.split(".");
  const sinCeros = dec.replace(/0+$/, "");
  return sinCeros ? `${ent},${sinCeros}` : ent;
}

export function formItemObra(i?: ItemComputo): FormItemObra {
  if (!i) return { descripcion: "", unidad: "", cantidad: "", materiales: "", mano_obra: "" };
  return {
    descripcion: i.descripcion,
    unidad: i.unidad ?? "",
    cantidad: i.cantidad === null ? "" : cantidadAInput(i.cantidad),
    materiales: i.precio_materiales === null ? "" : montoAInput(i.precio_materiales),
    mano_obra: i.precio_mano_obra === null ? "" : montoAInput(i.precio_mano_obra),
  };
}

/** "120,5" o "120.5" → "120.5"; hasta 3 decimales y sin separador de miles. Vacío → null; inválido → undefined. */
export function parseCantidad(texto: string): string | null | undefined {
  const t = texto.trim();
  if (!t) return null;
  if (!/^\d{1,9}([.,]\d{1,3})?$/.test(t)) return undefined;
  return t.replace(",", ".");
}

const ES_CERO = /^0+([.,]0{0,2})?$/;

/** Precio: como un monto, pero el 0 vale (un ítem que no cuesta nada). Vacío → null; inválido → undefined. */
function parsePrecio(texto: string): string | null | undefined {
  const t = texto.trim();
  if (!t) return null;
  if (ES_CERO.test(t)) return "0";
  return parseMonto(t) ?? undefined;
}

function erroresComunes(f: Pick<FormItem, "descripcion" | "unidad" | "cantidad">): Partial<Record<"descripcion" | "unidad" | "cantidad", string>> {
  const e: Partial<Record<"descripcion" | "unidad" | "cantidad", string>> = {};
  const desc = f.descripcion.trim();
  if (!desc) e.descripcion = "Escribí la descripción";
  else if (desc.length > 500) e.descripcion = "Hasta 500 caracteres";
  if (f.unidad.trim().length > 20) e.unidad = "Hasta 20 caracteres";
  if (parseCantidad(f.cantidad) === undefined) e.cantidad = "Ingresá un número (hasta 3 decimales, sin puntos de miles)";
  return e;
}

export function validarItem(f: FormItem): Partial<Record<keyof FormItem, string>> {
  const e: Partial<Record<keyof FormItem, string>> = erroresComunes(f);
  if (parsePrecio(f.precio) === undefined) e.precio = "Ingresá un precio válido";
  return e;
}

export function validarItemObra(f: FormItemObra): Partial<Record<keyof FormItemObra, string>> {
  const e: Partial<Record<keyof FormItemObra, string>> = erroresComunes(f);
  if (parsePrecio(f.materiales) === undefined) e.materiales = "Ingresá un precio válido";
  if (parsePrecio(f.mano_obra) === undefined) e.mano_obra = "Ingresá un precio válido";
  return e;
}

export interface PayloadItem {
  descripcion: string;
  unidad: string | null;
  cantidad: string | null;
  precio_unitario: string | null;
}

export function aPayloadItem(f: FormItem): PayloadItem {
  return {
    descripcion: f.descripcion.trim(),
    unidad: f.unidad.trim() || null,
    cantidad: parseCantidad(f.cantidad) ?? null,
    precio_unitario: parsePrecio(f.precio) ?? null,
  };
}

export interface PayloadItemObra {
  descripcion: string;
  unidad: string | null;
  cantidad: string | null;
  precio_materiales: string | null;
  precio_mano_obra: string | null;
}

export function aPayloadItemObra(f: FormItemObra): PayloadItemObra {
  return {
    descripcion: f.descripcion.trim(),
    unidad: f.unidad.trim() || null,
    cantidad: parseCantidad(f.cantidad) ?? null,
    precio_materiales: parsePrecio(f.materiales) ?? null,
    precio_mano_obra: parsePrecio(f.mano_obra) ?? null,
  };
}

/** cantidad × precio en centavos (BigInt, redondeo bancario como el backend). null si falta alguno o no es válido. */
function multiplicar(cantidadTexto: string, precioTexto: string): bigint | null {
  const cantidad = parseCantidad(cantidadTexto);
  const precio = parsePrecio(precioTexto);
  if (!cantidad || !precio) return null;
  const [ent = "0", dec = ""] = cantidad.split(".");
  const milesimas = BigInt(ent) * 1000n + BigInt((dec + "000").slice(0, 3));
  const producto = milesimas * aCentavos(precio); // centavos × 1000
  let centavos = producto / 1000n;
  const resto = producto % 1000n;
  if (resto > 500n || (resto === 500n && centavos % 2n === 1n)) centavos += 1n;
  return centavos;
}

/** Subtotal en vivo del form de una línea. null si falta algo. */
export function subtotalForm(f: FormItem): string | null {
  const c = multiplicar(f.cantidad, f.precio);
  return c === null ? null : deCentavos(c);
}

export interface SubtotalesObra {
  materiales: string | null;
  mano_obra: string | null;
  /** La suma de las dos partes (como el backend: así siempre cierran); null si no hay ninguna. */
  total: string | null;
}

/** "Materiales USD 300,00 · Mano de obra USD 165,00": las dos partes de un subtotal (las que hay); null sin ninguna. */
export function textoPartes(materiales: string | null, manoObra: string | null, moneda: Moneda = "USD"): string | null {
  const partes = [
    materiales === null ? null : `Materiales ${formatMoneda(materiales, moneda)}`,
    manoObra === null ? null : `Mano de obra ${formatMoneda(manoObra, moneda)}`,
  ].filter((p) => p !== null);
  return partes.length ? partes.join(" · ") : null;
}

/** Subtotales en vivo del form de un ítem de obra. */
export function subtotalesForm(f: FormItemObra): SubtotalesObra {
  const m = multiplicar(f.cantidad, f.materiales);
  const mo = multiplicar(f.cantidad, f.mano_obra);
  return {
    materiales: m === null ? null : deCentavos(m),
    mano_obra: mo === null ? null : deCentavos(mo),
    total: m === null && mo === null ? null : deCentavos((m ?? 0n) + (mo ?? 0n)),
  };
}

/** Pesos a USD con el tipo de cambio (ARS por USD, hasta 4 decimales), a centavos con redondeo bancario como `convertir` del backend. */
function aUsd(ars: string, tipoCambio: string): string {
  const [ent = "0", dec = ""] = tipoCambio.split(".");
  const diezmilesimas = BigInt(ent) * 10000n + BigInt((dec + "0000").slice(0, 4));
  const numerador = aCentavos(ars) * 10000n;
  let centavos = numerador / diezmilesimas;
  const resto2 = (numerador % diezmilesimas) * 2n;
  if (resto2 > diezmilesimas || (resto2 === diezmilesimas && centavos % 2n === 1n)) centavos += 1n;
  return deCentavos(centavos);
}

/** Los subtotales en pesos del form, en USD: cada parte pasada una vez y el total como su suma (como el backend). Sin tipo de cambio, null. */
export function subtotalesEnUsd(s: SubtotalesObra, tipoCambio: string | null): SubtotalesObra {
  if (tipoCambio === null || s.total === null) return { materiales: null, mano_obra: null, total: null };
  const m = s.materiales === null ? null : aUsd(s.materiales, tipoCambio);
  const mo = s.mano_obra === null ? null : aUsd(s.mano_obra, tipoCambio);
  return { materiales: m, mano_obra: mo, total: deCentavos((m === null ? 0n : aCentavos(m)) + (mo === null ? 0n : aCentavos(mo))) };
}

const HASTA_TRES_DECIMALES = new Intl.NumberFormat("es-AR", { maximumFractionDigits: 3 });

/** "120.500" con "m3" → "120,5 m3". */
export function formatCantidad(i: Pick<ItemComputo, "cantidad" | "unidad">): string {
  if (i.cantidad === null) return "—";
  const n = HASTA_TRES_DECIMALES.format(Number(i.cantidad));
  return i.unidad ? `${n} ${i.unidad}` : n;
}

// ------------------------------------------------------------------ importación de la planilla

/** Renglones de la vista previa (o del resultado) para mostrar antes de confirmar. */
export function lineasImportacion(r: ImportacionPresupuesto): string[] {
  const lista = (nombres: string[]) => nombres.join(", ");
  const lineas: string[] = [];
  const cargar = r.rubros_nuevos.length + r.rubros_existentes.length;
  if (cargar > 0) lineas.push(`${r.items} ${r.items === 1 ? "ítem" : "ítems"} en ${cargar} ${cargar === 1 ? "rubro" : "rubros"}.`);
  if (r.rubros_nuevos.length) lineas.push(`Rubros nuevos: ${lista(r.rubros_nuevos)}.`);
  if (r.rubros_existentes.length) lineas.push(`Rubros que ya estaban: ${lista(r.rubros_existentes)}.`);
  if (r.rubros_salteados.length) lineas.push(`Ya tienen ítems y no se tocan: ${lista(r.rubros_salteados)}.`);
  if (r.rubros_archivados.length) lineas.push(`Se archivan porque no están en la planilla y no se usan: ${lista(r.rubros_archivados)}.`);
  if (r.subtitulos.length) lineas.push(`Subtítulos que no son ítems (no se cargan): ${lista(r.subtitulos)}.`);
  if (cargar === 0) lineas.push("No hay nada nuevo para cargar.");
  return lineas;
}

// ------------------------------------------------------------------ comparación ítem por ítem con los presupuestos recibidos

export interface FilaComparacionItems {
  clave: string;
  descripcion: string;
  presupuestado: string | null;
  celdas: CeldaComparacion[];
  /** "Otras líneas" y "Total": sin presupuestado ni diferencia. */
  resumen: boolean;
}

/** Las filas de la tabla: un ítem por fila y, al final, "Otras líneas" (si alguna columna tiene) y el "Total" de cada presupuesto. */
export function filasComparacionItems(ficha: RubroPresupuesto): FilaComparacionItems[] {
  const c = ficha.comparacion_items;
  if (c.columnas.length === 0) return [];
  const nombres = new Map(ficha.items.map((i) => [i.id, i.descripcion]));
  const sinDiferencia = (usd: string | null): CeldaComparacion => ({ usd, diferencia: null });
  const filas: FilaComparacionItems[] = c.filas.map((f) => ({ clave: `item-${f.item_id}`, descripcion: nombres.get(f.item_id) ?? "", presupuestado: f.presupuestado, celdas: f.celdas, resumen: false }));
  if (c.otras.some((v) => v !== null)) filas.push({ clave: "otras", descripcion: "Otras líneas", presupuestado: null, celdas: c.otras.map(sinDiferencia), resumen: true });
  filas.push({ clave: "total", descripcion: "Total de las líneas", presupuestado: ficha.resumen.presupuesto, celdas: c.totales.map(sinDiferencia), resumen: true });
  return filas;
}

/** "200.00" → "+USD 200,00 sobre lo presupuestado"; "-100.00" → "USD 100,00 debajo"; "0.00" → "igual". */
export function textoDiferenciaItem(diferencia: string): string {
  if (/^-?0+(\.0+)?$/.test(diferencia)) return "Igual a lo presupuestado";
  return diferencia.startsWith("-") ? `${formatMoneda(diferencia.slice(1), "USD")} menos` : `${formatMoneda(diferencia, "USD")} más`;
}

/**
 * Las filas en el orden pedido (ids), con la regla del backend (`reglas.reordenar`): los pedidos ocupan, en ese
 * orden, los lugares que tenían; los que no vinieron quedan donde estaban. Para mostrar el orden nuevo antes de que
 * vuelva del servidor. `null` = como vinieron.
 */
export function aplicarOrden<T>(filas: T[], ids: number[] | null, id: (f: T) => number): T[] {
  if (ids === null) return filas;
  const porId = new Map(filas.map((f) => [id(f), f]));
  const pedidos = ids.filter((i) => porId.has(i));
  const enPedidos = new Set(pedidos);
  let siguiente = 0;
  return filas.map((f) => (enPedidos.has(id(f)) ? porId.get(pedidos[siguiente++]!)! : f));
}

/** Clave del rubro Trámites previos (Mati, 2026-10-30): si es el primero, los números van desde 0. */
export const CLAVE_PREVIOS = "previos";

/** El número del primer rubro: 0 si es Trámites previos, si no 1. */
export function primerNumero(rubros: { clave: string | null }[]): number {
  return rubros[0]?.clave === CLAVE_PREVIOS ? 0 : 1;
}

/** El número del ítem en el presupuesto: "rubro.ítem" por la posición de cada uno, desde 1 (Mati, 2026-10-28); el
 * rubro desde `primero` (0 con Trámites previos). */
export function numeroItem(posRubro: number, posItem: number, primero = 1): string {
  return `${posRubro + primero}.${posItem + 1}`;
}
